"use client";

import { useSyncExternalStore } from "react";
import { UnreadDot } from "@/components/unread-dot";
import { hasUnreadPost, nextSeen, seenFromCookieHeader } from "@/lib/blog/seen";

/** What the new-post mark compares (lib/blog/seen.ts). */
export interface BlogUnread {
  /** The seen date the server read from the request; null when the render had
   *  no cookie — always so on a page nginx shares between visitors
   *  (lib/marketing-cache.ts), which is forwarded without any. */
  readonly seen: string | null;
  /** The newest post's date (or, for one post's dot, that post's). */
  readonly latest: string | null;
  /** On a post page, the post being read: it counts as seen already. */
  readonly reading: string | null;
}

const noSubscription = () => () => undefined;

/**
 * Is there a post this visitor has not seen? The server's answer while
 * rendering and hydrating, so the HTML of a personal render is unchanged; the
 * browser's own cookie after, which is what decides on a shared render.
 */
export const useBlogUnread = ({ seen, latest, reading }: BlogUnread): boolean => {
  const current = useSyncExternalStore(
    noSubscription,
    () => seenFromCookieHeader(document.cookie) ?? seen,
    () => seen,
  );
  // The post being read is written to the cookie by the page AFTER this
  // render, so without this the dot stayed up beside the very post it marked.
  return hasUnreadPost(reading === null ? current : nextSeen(current, reading), latest);
};

/** UnreadDot, shown when `useBlogUnread` says so. */
export function BlogUnreadDot({ unread, label, className }: { unread: BlogUnread; label: string; className?: string }) {
  return useBlogUnread(unread) ? <UnreadDot label={label} className={className} /> : null;
}
